import { ChartDateValue } from "types/chart";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import { ROLES } from "types/user";

export const FEEDBACK_EXPIRATION_DAYS = 8;
export const FEEDBACK_EXPIRATION_TIME_MS =
  FEEDBACK_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
export const FEEDBACK_EXPIRED_HIDE_AFTER_MS = 24 * 60 * 60 * 1000;

type FeedbackVisibilityFields = Pick<
  FeedbackType,
  "status" | "observation_date" | "created_at"
>;

const parseTimestamp = (value?: string | null) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

/**
 * Coach response window starts when the request exists, not when the case
 * happened. Use the later of created_at and observation_date so a just-
 * submitted request with a backdated case still appears on Home.
 */
export const feedbackExpirationStart = (
  feedback: Pick<FeedbackType, "observation_date" | "created_at">,
) => {
  const createdAt = parseTimestamp(feedback.created_at);
  const observationDate = parseTimestamp(feedback.observation_date);
  if (createdAt != null && observationDate != null) {
    return Math.max(createdAt, observationDate);
  }
  return createdAt ?? observationDate;
};

export const isFeedbackExpired = (
  feedback: Pick<FeedbackType, "observation_date" | "created_at">,
  now = new Date(),
) => {
  const start = feedbackExpirationStart(feedback);
  if (start == null) return false;
  return now.getTime() - start > FEEDBACK_EXPIRATION_TIME_MS;
};

export const isFeedbackExpiredPastHideWindow = (
  feedback: Pick<FeedbackType, "observation_date" | "created_at">,
  now = new Date(),
) => {
  const start = feedbackExpirationStart(feedback);
  if (start == null) return false;
  const hideAt = start + FEEDBACK_EXPIRATION_TIME_MS + FEEDBACK_EXPIRED_HIDE_AFTER_MS;
  return now.getTime() > hideAt;
};

export const FEEDBACK_ACCESS_TITLE = "You can't view this feedback content";

export const FEEDBACK_ACCESS_MESSAGE =
  "Feedback details are shared only with the learner and the coach the request was addressed to. Selecting a coach is how the learner consents to share this information — you do not need to be their mentor. If you were not added as the coach on this request, you will not see the content.";

export const getFeedbackDetailPath = (
  feedbackId: string,
  role?: string,
  organizationId?: string,
) => {
  if (
    organizationId &&
    (role === ROLES.ADMIN ||
      role === ROLES.DIRECTOR ||
      role === ROLES.MANAGER)
  ) {
    return `/organization/${organizationId}/feedback/${feedbackId}`;
  }
  if (role === ROLES.COACH || role === ROLES.STUDENT) {
    return `/feedback-portal/${feedbackId}`;
  }
  return `/feedback/${feedbackId}`;
};

export const LEARNER_ACCESS_TITLE = "You can't view this learner's details";

export const LEARNER_ACCESS_MESSAGE =
  "Learner details and feedback are shared only with mentors and with coaches the learner addressed a request to. Selecting a coach on a feedback request is how the learner consents to share this information.";

export const isHiddenFeedbackStatus = (status?: string) =>
  status === FeedbackStatus.ARCHIVED || status === FeedbackStatus.HIDDEN;

export const shouldHideFeedback = (
  feedback: FeedbackVisibilityFields,
  now = new Date(),
) => {
  if (isHiddenFeedbackStatus(feedback.status)) return true;
  if (
    feedback.status !== FeedbackStatus.PENDING &&
    feedback.status !== FeedbackStatus.EXPIRED
  ) {
    return false;
  }
  return isFeedbackExpiredPastHideWindow(feedback, now);
};

export const isFeedbackMissingInfo = (feedback: FeedbackType) => {
  return (
    feedback.course === null ||
    feedback.course === "" ||
    feedback.animal_sex === "" ||
    feedback.site === null ||
    feedback.species === null
  );
};

export const getFeedbackStatus = (feedback: FeedbackType) => {
  if (isHiddenFeedbackStatus(feedback.status)) {
    return feedback.status;
  }
  if (
    feedback.status === FeedbackStatus.PENDING &&
    isFeedbackExpired(feedback)
  ) {
    return FeedbackStatus.EXPIRED;
  }
  if (
    feedback.status === FeedbackStatus.PENDING &&
    isFeedbackMissingInfo(feedback)
  ) {
    return FeedbackStatus.MISSING_INFO;
  }
  return feedback.status;
};

export const filterFeedbackStats = (
  feedbacks: FeedbackType[],
  withData = false,
): ChartDateValue[] => {
  return feedbacks
    .filter((f) => f.status === FeedbackStatus.DONE)
    .map((f) => {
      // Get coach_rating from feedback_skills (first one for non-ITER, or aggregate for ITER)
      const coachRating = f.feedback_skills?.[0]?.coach_rating || null;
      if (withData) {
        return { date: f.observation_date, value: coachRating, data: f };
      }
      return { date: f.observation_date, value: coachRating };
    }) as ChartDateValue[];
};

export const getFeedbackForChart = (
  feedbacks: ChartDateValue[],
): ChartDateValue[] => {
  const dates = feedbacks
    .filter((item) => item.date && item.date.trim() !== "")
    .map((item) => new Date(item.date as string));

  if (dates.length > 0) {
    // add initial point with 0 value
    const minDate = new Date(Math.min(...dates.map((date) => date.getTime())));

    const previousDay = new Date(minDate);
    previousDay.setDate(previousDay.getDate() - 1);

    return [
      {
        date: previousDay.toISOString().split("T")[0],
        value: 0,
      },
      ...feedbacks,
    ];
  }

  return feedbacks;
};
